import { requestReport } from './reportsResource';
import { schoolSessionKey } from './schoolSummaryApi';
import { DIAGNOSTIC_IDS, diagnosticScoreKey } from '../../Diagnostic/diagnosticAvailability';

// Saved scores (2026-09-25): a school of 2,052 is 103 score pages of 20, and
// each page rebuilds the roster on the server. They used to be re-read every
// minute, and every live poll waited for that read. Now all pages are re-read
// at most every 5 minutes or on "Refresh". In between, only the pages that hold
// a newly finished student, or a student whose score should be saved but is
// not (checked at most once a minute), are read again.
const LIVE_MS = 15000, MISSING_MS = 60000, SCORES_MS = 300000, ROSTER_MS = 300000, PAGE = 20;
const URL = process.env.REACT_APP_ADMIN_DIAGNOSTICS_URL || 'https://gyxyzs5421.execute-api.us-east-1.amazonaws.com/Production/adminDiagnosticControls';
const cancelled = () => new DOMException('Cancelled', 'AbortError');
export const diagnosticsKey = (auth, school, rawSchool) => {
  const session = schoolSessionKey(auth);
  return session && school && rawSchool ? JSON.stringify([session, school, rawSchool]) : null;
};

export async function requestDiagnostic(auth, body, { signal } = {}) {
  if (!auth?.user?.accessToken) throw Object.assign(Error('Sign in again to use Diagnostics.'), { status: 401 });
  const post = token => fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: token }, body: JSON.stringify(body), signal });
  let response = await post(auth.user.accessToken);
  if (response.status === 401 && auth.refreshToken) {
    const token = await auth.refreshToken();
    if (signal?.aborted) throw cancelled();
    if (token) response = await post(token);
  }
  let data;
  try { data = await response.json(); } catch { throw Error('The diagnostic response could not be read.'); }
  if (!response.ok) throw Object.assign(Error(data?.error || `Diagnostics could not load (${response.status}).`), { status: response.status });
  return data;
}

// A student whose live state says a saved score should exist, but the saved
// copy has none yet: they finished the test. Both sections are scored together
// when Math ends (the English-at-the-break score was removed 2026-10-03), so
// a student still in Break or Math is not missing anything.
export function lacksScore(student, score) {
  return student.status === 'Done' && (score?.math == null || score?.english == null);
}

const empty = key => ({ key, entries: {} });
// Same students in the same order: every score page still holds the same people.
const sameStudents = (a, b) => a.students.length === b.students.length && a.students.every((s, i) => s.id === b.students[i].id && s.name === b.students[i].name);
// Memory only. Live state expires after 15 seconds; both saved test scores share
// one cache (see the note on SCORES_MS). Session changes abort reads and
// discard every cached row.
export function createDiagnosticsResource(report = requestReport, monitor = requestDiagnostic, now = Date.now) {
  let state = empty(null), auth, school, rawSchool, session, generation = 0;
  let controller = new AbortController(), roster, rosterAt = 0, rosterTask, scores, scoresAt = 0, checkedAt = 0, scoresTask;
  const listeners = new Set(), pending = new Map();
  const emit = patch => { state = { ...state, ...patch }; listeners.forEach(fn => fn()); };
  const entry = (id, patch) => emit({ entries: { ...state.entries, [id]: { ...state.entries[id], ...patch } } });
  const alive = g => { if (generation !== g || controller.signal.aborted) throw cancelled(); };
  const call = (action, params) => report(action, params, { auth, school, signal: controller.signal });

  async function loadRoster(force, g) {
    if (rosterTask) return rosterTask;
    if (!force && roster && now() - rosterAt < ROSTER_MS) return roster;
    const task = call('getReportRoster', {}).then(value => {
      alive(g);
      if (!Array.isArray(value.students) || value.students.length > 5000 || value.students.some(s => typeof s.id !== 'string' || !s.id || typeof s.name !== 'string') || new Set(value.students.map(s => s.id)).size !== value.students.length || !/^[a-f0-9]{64}$/.test(value.rosterVersion)) throw Error('The school roster is incomplete.');
      // A new version with the same students (a class join, 2026-10-07) keeps
      // the saved scores and any read in progress: the roster is updated in
      // place, so pages still to come use the new version. Different students
      // start a new roster; the old scores stay on screen (they are per
      // student) until the full read on the new roster replaces them.
      if (roster && roster.rosterVersion !== value.rosterVersion && sameStudents(roster, value)) {
        if (scores?.version === roster.rosterVersion) scores = { ...scores, version: value.rosterVersion };
        if (scoresTask?.version === roster.rosterVersion) scoresTask.version = value.rosterVersion;
        Object.assign(roster, value); rosterAt = now(); return roster;
      }
      roster = value; rosterAt = now(); return value;
    }).finally(() => { if (generation === g && rosterTask === task) rosterTask = null; });
    rosterTask = task; return task;
  }

  // A 409 means the roster version moved under the read. The roster is read
  // again: same students, so it was updated in place and this page is asked
  // again (at most twice); different students end this read with the 409,
  // which load() handles without an error.
  async function readScorePage(people, offset, g) {
    for (let attempt = 0; ; attempt++) {
      alive(g);
      const sent = people.rosterVersion;
      let page;
      try { page = await call('getDiagnosticPage', { offset, rosterVersion: sent }); }
      catch (error) {
        if (error.status !== 409 || attempt >= 2) throw error;
        await loadRoster(true, g); alive(g);
        if (roster !== people) throw error;
        continue;
      }
      alive(g);
      const count = Math.min(PAGE, people.students.length - offset), next = offset + count;
      if (page.rosterVersion !== sent || page.offset !== offset || page.total !== people.students.length || !Array.isArray(page.students) || page.students.length !== count || page.nextOffset !== (next < page.total ? next : null)) throw Error('A diagnostic score page is missing.');
      return page.students;
    }
  }

  // Every page, 2 at a time. Concurrent callers share one read.
  function loadScores(people, g) {
    if (scoresTask?.version === people.rosterVersion) return scoresTask.promise;
    const task = (async () => {
      const rows = [];
      const read = async offset => { rows.push(...await readScorePage(people, offset, g)); };
      for (let i = 0; i < people.students.length; i += 2 * PAGE) await Promise.all([i, i + PAGE].filter(n => n < people.students.length).map(read));
      alive(g);
      const ids = new Set(people.students.map(s => s.id));
      if (rows.length !== ids.size || new Set(rows.map(s => s.id)).size !== ids.size || rows.some(s => !ids.has(s.id) || !s.diagnosticScores?.d2 || !s.diagnosticScores?.d3)) throw Error('The saved scores do not match the school roster.');
      const map = new Map(rows.map(s => [s.id, s.diagnosticScores]));
      if (roster?.rosterVersion === people.rosterVersion) { scores = { version: people.rosterVersion, rows: map }; scoresAt = checkedAt = now(); }
      return map;
    })().finally(() => { if (generation === g && scoresTask?.promise === task) scoresTask = null; });
    scoresTask = { version: people.rosterVersion, promise: task }; return task;
  }

  // Only the pages that hold these students. Pages follow the roster order,
  // so the student at roster index i is on the page at offset i - i % 20.
  // When that is most of the pages anyway, one full read replaces it.
  async function refreshScores(people, ids, cache, g) {
    const index = new Map(people.students.map((s, i) => [s.id, i]));
    const offsets = [...new Set([...ids].map(id => index.get(id)).filter(i => i !== undefined).map(i => i - (i % PAGE)))].sort((a, b) => a - b);
    if (offsets.length * 2 > Math.ceil(people.students.length / PAGE)) return loadScores(people, g);
    const found = [];
    const read = async offset => {
      const page = await readScorePage(people, offset, g), expected = new Set(people.students.slice(offset, offset + PAGE).map(s => s.id));
      if (new Set(page.map(s => s.id)).size !== expected.size || page.some(s => !expected.has(s.id) || !s.diagnosticScores?.d2 || !s.diagnosticScores?.d3)) throw Error('The saved scores do not match the school roster.');
      found.push(...page);
    };
    for (let i = 0; i < offsets.length; i += 2) await Promise.all(offsets.slice(i, i + 2).map(read));
    alive(g);
    // Merge into the newest cache, so two tests refreshing at once keep both.
    const current = scores?.version === people.rosterVersion ? scores : cache;
    const map = new Map(current.rows);
    found.forEach(s => map.set(s.id, s.diagnosticScores));
    if (roster?.rosterVersion === people.rosterVersion) { scores = { version: people.rosterVersion, rows: map }; checkedAt = now(); }
    return map;
  }

  // What this poll needs from the saved scores: the cache as it is, a few
  // pages (`ids`), every page (`full`), or a full read already running (`wait`).
  function scorePlan(people, rows, scoreKey, full, newlyDone, cache) {
    if (scoresTask?.version === people.rosterVersion) return { wait: scoresTask.promise };
    if (full || !cache || now() - scoresAt >= SCORES_MS) return { full: true };
    const ids = new Set(newlyDone);
    if (now() - checkedAt >= MISSING_MS) rows.forEach(s => { if (lacksScore(s, cache.rows.get(s.studentId)?.[scoreKey])) ids.add(s.studentId); });
    return ids.size ? { ids } : { rows: cache.rows };
  }

  const api = {
    getSnapshot: () => state,
    subscribe: fn => { listeners.add(fn); return () => listeners.delete(fn); },
    resetSession(value) { if (schoolSessionKey(value) !== session) api.bind(null, null, null); },
    bind(value, id, legacy) {
      const key = diagnosticsKey(value, id, legacy);
      auth = value; school = id; rawSchool = legacy; session = schoolSessionKey(value);
      if (state.key === key) return;
      generation++; controller.abort(); controller = new AbortController();
      roster = scores = rosterTask = scoresTask = null; rosterAt = scoresAt = checkedAt = 0; pending.clear(); emit(empty(key));
    },
    // `force`: true re-reads the roster, the live monitor and every saved
    // score page ("Refresh"). 'live' re-reads the live monitor only, with the
    // usual score rules (after a pause, move or time change, 2026-09-25).
    // `retried` is internal: the one immediate retry after a roster change.
    async load(assignmentId, force = false, retried = false) {
      if (!state.key || !DIAGNOSTIC_IDS.includes(assignmentId)) return;
      if (pending.has(assignmentId)) {
        const g = generation, current = pending.get(assignmentId);
        if (!force) return current;
        // A control may finish while a poll is still reading the old state.
        // Refresh again after that read, so its result cannot stand in for
        // the required read after the control was applied.
        await current;
        if (generation !== g) return;
        return api.load(assignmentId, force);
      }
      const previous = state.entries[assignmentId];
      if (!force && previous?.data && now() - previous.receivedAt < LIVE_MS) return previous.data;
      const g = generation, full = force === true;
      let rosterMoved = false;
      entry(assignmentId, { loading: true, error: '' });
      const task = (async () => {
        const people = await loadRoster(full, g); alive(g);
        const all = [], chunks = [];
        for (let i = 0; i < people.students.length; i += 500) chunks.push(people.students.slice(i, i + 500).map(s => s.id));
        const monitorAuth = auth, schoolName = rawSchool, signal = controller.signal;
        const readMonitor = studentIds => monitor(monitorAuth, { action: 'get_monitor_detail', assignmentId, schoolName, studentIds }, { signal });
        // The existing monitor accepts at most 500 explicit student IDs.
        for (let i = 0; i < chunks.length; i += 2) {
          alive(g);
          all.push(...await Promise.all(chunks.slice(i, i + 2).map(readMonitor)));
        }
        alive(g);
        const rows = all.flatMap(page => page.students || []), ids = new Set(people.students.map(s => s.id));
        if (rows.length !== ids.size || new Set(rows.map(s => s.studentId)).size !== ids.size || rows.some(s => !ids.has(s.studentId))) throw Error('The live monitor returned an incomplete student list. Refresh to try again.');
        // Students who finished since the last read: one Map lookup each (was
        // a find() inside some(), about 4 million steps at 2,052 students).
        const before = previous?.data ? new Map(previous.data.students.map(p => [p.studentId, p.status])) : null;
        const newlyDone = before ? rows.filter(s => s.status === 'Done' && before.get(s.studentId) !== 'Done').map(s => s.studentId) : [];
        const byId = new Map(rows.map(s => [s.studentId, s])), scoreKey = diagnosticScoreKey(assignmentId);
        const build = (saved, extra) => ({ classStatus: all.some(p => p.classStatus === 'paused') ? 'paused' : all[0]?.classStatus, students: people.students.map(person => {
          const score = saved?.get(person.id)?.[scoreKey];
          return { ...byId.get(person.id), studentId: person.id, name: person.name, score: score ? { math: score.math, reading: score.english } : null };
        }), ...extra });
        const rosterChanged = () => Object.assign(Error('The school roster changed. Refresh to use the current students.'), { status: 409 });
        const cache = scores?.version === people.rosterVersion ? scores : null;
        // Scores saved under an older roster are still right for every
        // student they hold: shown until the read on this roster lands.
        const shown = cache?.rows || scores?.rows;
        const plan = scorePlan(people, rows, scoreKey, full, newlyDone, cache);
        let saved = plan.rows, scoreError = '';
        if (!saved) {
          // A full score read takes a while on a big school: show the live
          // rows and controls now, with the scores already known.
          if (plan.full || plan.wait) {
            if (roster?.rosterVersion !== people.rosterVersion) throw rosterChanged();
            entry(assignmentId, { data: build(shown, cache ? {} : { scoresPending: true }), receivedAt: now() });
          }
          try { saved = await (plan.wait || (plan.full ? loadScores(people, g) : refreshScores(people, plan.ids, cache, g))); }
          catch (error) {
            if (error.name === 'AbortError' || [401, 403, 409].includes(error.status)) throw error;
            saved = shown;
            scoreError = shown
              ? 'Saved scores could not be refreshed. The live controls are available. Refresh to try again.'
              : 'Saved scores could not be loaded. The live controls are available. Refresh to try again.';
          }
        }
        alive(g);
        if (roster?.rosterVersion !== people.rosterVersion) throw rosterChanged();
        const data = build(saved);
        entry(assignmentId, { data, receivedAt: now(), error: scoreError }); return data;
      })().catch(error => {
        if (generation === g && error.name !== 'AbortError') {
          // The students changed during the read (2026-10-07): no error and
          // nothing cleared. The page keeps what it shows and reads once more
          // on the new roster right away; after that, the next poll.
          if (error.status === 409) { rosterMoved = true; return; }
          if ([401, 403].includes(error.status)) { roster = scores = null; rosterAt = scoresAt = 0; emit({ entries: {} }); }
          entry(assignmentId, { error: error.message || 'Could not load Diagnostics.' });
        }
      }).finally(() => {
        if (generation === g) { pending.delete(assignmentId); entry(assignmentId, { loading: false, loaded: true }); }
      });
      pending.set(assignmentId, task);
      return task.then(data => (rosterMoved && !retried && generation === g ? api.load(assignmentId, force === true || 'live', true) : data));
    },
  };
  return api;
}
export const diagnosticsResource = createDiagnosticsResource();
